import { useSyncExternalStore, useCallback } from "react";
const records = new Map();
function makeSnapshot(rec) {
    const duration = rec.duration > 0 && Number.isFinite(rec.duration) ? rec.duration : 0;
    const progress = duration > 0 ? Math.min(1, rec.currentTime / duration) : 0;
    return { isPlaying: rec.isPlaying, progress, duration };
}
function notify(rec) {
    rec.snapshot = makeSnapshot(rec);
    for (const listener of rec.listeners)
        listener();
}
function pauseAllExcept(blockId) {
    for (const [id, rec] of records) {
        if (id === blockId)
            continue;
        if (rec.isPlaying)
            rec.audio.pause();
    }
}
function getOrCreate(blockId, url, initialDuration) {
    const existing = records.get(blockId);
    if (existing && existing.url === url)
        return existing;
    if (existing) {
        existing.audio.pause();
        existing.audio.src = "";
    }
    const audio = new Audio();
    // DISABLED NETWORK FETCH: preload="metadata" causes the browser to fetch
    // the audio file header on every audio block mount to read duration/codec.
    // Switched to "none" so the network call only happens when the user
    // clicks play. content.duration is already persisted on the block, so
    // the waveform/duration UI is unaffected.
    // audio.preload = "metadata";
    audio.preload = "none";
    audio.src = url;
    const rec = {
        audio,
        url,
        isPlaying: false,
        currentTime: 0,
        duration: initialDuration > 0 && Number.isFinite(initialDuration) ? initialDuration : 0,
        listeners: new Set(),
        snapshot: { isPlaying: false, progress: 0, duration: 0 },
    };
    rec.snapshot = makeSnapshot(rec);
    audio.addEventListener("play", () => {
        rec.isPlaying = true;
        notify(rec);
    });
    audio.addEventListener("pause", () => {
        rec.isPlaying = false;
        notify(rec);
    });
    audio.addEventListener("ended", () => {
        rec.isPlaying = false;
        rec.currentTime = 0;
        notify(rec);
    });
    audio.addEventListener("timeupdate", () => {
        rec.currentTime = audio.currentTime;
        notify(rec);
    });
    audio.addEventListener("loadedmetadata", () => {
        if (Number.isFinite(audio.duration) && audio.duration > 0) {
            rec.duration = audio.duration;
            notify(rec);
        }
    });
    records.set(blockId, rec);
    return rec;
}
export function useAudioPlayback(blockId, audioUrl, initialDuration = 0) {
    const rec = getOrCreate(blockId, audioUrl, initialDuration);
    const subscribe = useCallback((cb) => {
        rec.listeners.add(cb);
        return () => {
            rec.listeners.delete(cb);
        };
    }, [rec]);
    const getSnapshot = useCallback(() => rec.snapshot, [rec]);
    const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    const toggle = useCallback(() => {
        if (rec.isPlaying) {
            rec.audio.pause();
        }
        else {
            pauseAllExcept(blockId);
            void rec.audio.play().catch(() => {
                rec.isPlaying = false;
                notify(rec);
            });
        }
    }, [rec, blockId]);
    const seekTo = useCallback((ratio) => {
        const clamped = Math.max(0, Math.min(1, ratio));
        const dur = rec.duration > 0 ? rec.duration : rec.audio.duration;
        if (!dur || !Number.isFinite(dur))
            return;
        rec.audio.currentTime = clamped * dur;
        rec.currentTime = clamped * dur;
        notify(rec);
    }, [rec]);
    return {
        isPlaying: snapshot.isPlaying,
        progress: snapshot.progress,
        duration: snapshot.duration,
        toggle,
        seekTo,
    };
}
